import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { Clock, RefreshCw, Timer, UserCheck, Users } from 'lucide-react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import ProgressBar from '../../components/ProgressBar';
import { formatDateTime, formatDuration } from '../../utils/format';

interface LearnerRow {
  user: string;
  name?: string;
  email: string;
  role?: string;
  timeSpentSeconds: number;
  progressPercentage: number;
  lastAccessed: string | null;
}

interface CourseTimeResponse {
  courseId: string;
  title: string;
  learners: LearnerRow[];
  totals: { learners: number; totalSeconds: number; avgSeconds: number };
}

interface CourseOption {
  _id: string;
  title: string;
}

/**
 * Temps passé par apprenant / formation (sprint « traçabilité apprenants » — 01/10/2026).
 * Route : /admin/time-spent — données : GET /api/admin/analytics/course-time
 */
const AdminTimeSpent: React.FC = () => {
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [courseId, setCourseId] = useState('');
  const [data, setData] = useState<CourseTimeResponse | null>(null);
  const [globalTotals, setGlobalTotals] = useState<{ courses: number; learners: number; totalSeconds: number } | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCourses = async () => {
    try {
      const response = await api.get('/admin/courses');
      const list: CourseOption[] = response.data?.courses || [];
      setCourses(list);
      if (list.length > 0) {
        setCourseId((current) => current || list[0]._id);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Erreur lors du chargement des formations');
    }
  };

  const loadTimeSpent = useCallback(async () => {
    try {
      setLoading(true);
      if (courseId) {
        const response = await api.get('/admin/analytics/course-time', { params: { courseId } });
        setData(response.data);
        setGlobalTotals(null);
      } else {
        const response = await api.get('/admin/analytics/course-time');
        const list: CourseTimeResponse[] = response.data?.courses || [];
        const learners = list.flatMap((course) => course.learners || []);
        setData({
          courseId: '',
          title: 'Toutes les formations',
          learners,
          totals: {
            learners: learners.length,
            totalSeconds: response.data?.totals?.totalSeconds || 0,
            avgSeconds: learners.length > 0
              ? Math.round((response.data?.totals?.totalSeconds || 0) / learners.length)
              : 0
          }
        });
        setGlobalTotals(response.data?.totals || null);
      }
    } catch (error) {
      console.error('Error loading time spent:', error);
      toast.error('Erreur lors du chargement du temps passé');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    loadTimeSpent();
  }, [loadTimeSpent]);

  const learners = data?.learners || [];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Temps passé par formation</h1>
            <p className="text-sm text-gray-500 mt-1">
              Temps réellement passé par apprenant, mesuré pendant la consultation des cours
            </p>
          </div>
          <button
            onClick={loadTimeSpent}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraîchir
          </button>
        </div>

        {/* Sélection de la formation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Formation</label>
          <select
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="">Toutes les formations</option>
            {courses.map((course) => (
              <option key={course._id} value={course._id}>{course.title}</option>
            ))}
          </select>
        </div>

        {/* Totaux */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Users className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Apprenants</p>
                <p className="text-2xl font-bold text-gray-900">{data?.totals.learners ?? 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Timer className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Temps total</p>
                <p className="text-2xl font-bold text-gray-900">{formatDuration(data?.totals.totalSeconds ?? 0)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Clock className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Temps moyen / apprenant</p>
                <p className="text-2xl font-bold text-gray-900">{formatDuration(data?.totals.avgSeconds ?? 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {globalTotals && (
          <p className="text-sm text-gray-500">
            {globalTotals.courses} formation(s) — {globalTotals.learners} apprenant(s) au total.
          </p>
        )}

        {/* Tableau par apprenant */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{data?.title || 'Formation'}</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            </div>
          ) : learners.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun apprenant inscrit</h3>
              <p className="text-gray-500">Assignez des apprenants à cette formation pour suivre leur temps de travail.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Apprenant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Temps passé</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progression</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dernier accès</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {learners.map((learner) => (
                    <tr key={learner.user} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{learner.name || learner.email}</div>
                        {learner.name && <div className="text-sm text-gray-500">{learner.email}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDuration(learner.timeSpentSeconds)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-64">
                        <ProgressBar
                          progressPercentage={learner.progressPercentage || 0}
                          size="sm"
                          showLabel={false}
                        />
                        <span className="text-xs text-gray-500">{learner.progressPercentage || 0} %</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {learner.lastAccessed ? formatDateTime(learner.lastAccessed) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminTimeSpent;
